import { Tooltip } from '@material-ui/core';
import { MaterialTableProps } from 'material-table';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { confirmInvoiceFromApi } from '../../../../api/accounting';
import ActionsMenu from '../../../../components/Layout/Menus/ActionsMenu/ActionsMenu';
import TableLayout from '../../../../components/Layout/TableLayout';
import { useButtonAction } from '../../../../helpers/customHooks/useHookMethod';
import { formatNumber } from '../../../../helpers/formatNumber';
import { tFormKey, tKey } from '../../../../helpers/translate';
import { InvoiceData, InvoiceListItem } from '../../../../redux/accounting/definitions';
import { selectLoading } from '../../../../redux/accounting/selectors';
import { showSnackbar } from '../../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../../redux/FeedbackAPI/definitions';
import { AppState } from '../../../../redux/root-reducer';
import { buttonsInvoice } from '../../../Entities/Invoices/invoiceButtons';
import AccountingSubmenu from '../../AccountingSubmenu';
import useInvoiceList from '../hooks/useInvoiceList';
import useInvoiceListSelection from '../hooks/useInvoiceListSelection';
import InvoicesActionsModal from './InvoiceListActionsModal/InvoicesActionsModal';
import InvoicesList from './InvoicesList';

interface AcceptedInvoicesListProps {
  accountingLoading: boolean;
  downloadInvoice: (file_id: number, filename: string) => void;
  showSnackbar: (message: string, severity: SeveritySnackbar) => void;
}

const AcceptedInvoicesList: React.FC<AcceptedInvoicesListProps> = ({ accountingLoading, showSnackbar }) => {
  const { loading, invoices, errorListMessage, removeInvoices } = useInvoiceList('accepted', accountingLoading);
  const {
    selectedInvoices,
    showModal,
    openModal,
    closeModal,
    addSelectedInvoices,
    removeSelectedInvoices,
  } = useInvoiceListSelection();

  const buttonConfirm = buttonsInvoice().buttonOnConfirm;
  buttonConfirm.hidden = false;

  useButtonAction(buttonConfirm, openModal, { disabled: selectedInvoices.length === 0 });

  useEffect(() => {
    if (errorListMessage) {
      showSnackbar(errorListMessage, 'error');
    }
  }, [errorListMessage, showSnackbar]);

  const tableData: MaterialTableProps<InvoiceListItem> = {
    title: tFormKey('acceptedInvoicesList.title'),
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        render: rowData => (
          <Tooltip title={rowData.reference_number}>
            <span>{rowData.reference_number}</span>
          </Tooltip>
        ),
        //@ts-ignore
        width: '110px',
      },
      {
        field: 'entity_name',
        title: tKey('Proveedor'),
        render: rowData => (
          <Tooltip title={rowData.entity_name}>
            <span>{rowData.entity_name}</span>
          </Tooltip>
        ),
        //@ts-ignore
        width: '280px',
      },
      {
        field: 'invoice_date',
        title: tFormKey('Fecha'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.invoice_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'total_amount',
        title: tKey('Total'),
        type: 'numeric',
        render: (rowData: InvoiceListItem) => <div>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>,
        //@ts-ignore
        width: '110px',
      },
      {
        field: 'center_costs',
        title: tKey('Centro de coste'),
        render: rowData => (
          <Tooltip title={rowData.center_costs}>
            <span>{rowData.center_costs}</span>
          </Tooltip>
        ),
        //@ts-ignore
        width: '200px',
      },
      {
        field: 'account_number',
        title: tKey('IBAN'),
        render: rowData => (
          <Tooltip title={rowData.account_number}>
            <span>{rowData.account_number}</span>
          </Tooltip>
        ),
        //@ts-ignore
        //width: '200px',
      },
    ],
    data: invoices,
    options: {
      tableLayout: 'auto',
      pageSize: 50,
      pageSizeOptions: [20, 50, 100],
      actionsColumnIndex: -1,
      selection: true,
    },
  };

  tableData.isLoading = loading || accountingLoading;

  const confirmInvoice = async (invoiceData: InvoiceData) => {
    await confirmInvoiceFromApi(invoiceData);
  };

  const closeInvoicesDone = (processedInvoices: number[]) => {
    removeInvoices(processedInvoices);
    removeSelectedInvoices(processedInvoices);
    closeModal();
  };

  const renderActionsModal = (): JSX.Element | undefined => {
    return showModal ? (
      <InvoicesActionsModal
        show={showModal}
        handleClose={closeModal}
        invoices={selectedInvoices}
        actionName={tKey('invoice.actions.confirm')}
        invoiceTransition={confirmInvoice}
        handleInvoicesDone={closeInvoicesDone}
      />
    ) : (
      undefined
    );
  };

  return (
    <>
      <TableLayout
        leftSubmenu={<AccountingSubmenu selected={'accepted_invoices'} submenu={tKey('Facturas servicios')} />}
        rightSubmenu={<ActionsMenu actionsButtons={[buttonConfirm]} />}
      >
        <InvoicesList tableData={tableData} onSelectionChange={addSelectedInvoices} downloadAction />
      </TableLayout>
      {renderActionsModal()}
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  accountingLoading: selectLoading(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  showSnackbar: (message: string, severity: SeveritySnackbar): void => dispatch(showSnackbar(message, severity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AcceptedInvoicesList);
